import { BLOCK_URL_BASE_LIST } from '@/presession/authentication/types';
import {
  ILogger,
  WarpDriveLoginType,
  WSBrokerServiceConstants,
} from '@amzn/work-spaces-web-client-core-lib';

/**
 * Utility class for detecting login types from URLs
 */
export class LoginTypeDetector {
  /**
   * Main public method that detects the login type based on the URL pattern.
   * This method handles both direct login URLs and block URLs with redirect parameters.
   *
   * For direct login URLs, it matches against known patterns for WD_LOGIN and UNIFIED_LOGIN.
   * For block URLs (connectivity.amazonworkspaces.com/block), it delegates to detectBlockType
   * to analyze the redirectUrl parameter.
   *
   * @param url - The URL to analyze
   * @param logger - Optional logger for logging detection results
   * @returns The detected login type (WD_LOGIN, UNIFIED_LOGIN, HARD_BLOCK, or UNKNOWN)
   */
  static detectLoginTypeFromUrl(
    url: string | undefined,
    logger?: ILogger
  ): WarpDriveLoginType {
    if (!url) {
      logger?.warn('Empty URL provided');
      return WarpDriveLoginType.UNKNOWN;
    }

    switch (true) {
      case WSBrokerServiceConstants.WD_LOGIN_PATTERNS.some((pattern) =>
        url.includes(pattern)
      ):
        logger?.info(`Detected WD_LOGIN from URL: ${url}`);
        return WarpDriveLoginType.WD_LOGIN;

      case WSBrokerServiceConstants.UNIFIED_LOGIN_PATTERNS.some((pattern) =>
        url.includes(pattern)
      ):
        logger?.info(`Detected UNIFIED_LOGIN from URL: ${url}`);
        return WarpDriveLoginType.UNIFIED_LOGIN;

      case BLOCK_URL_BASE_LIST.some((blockUrl) => url.includes(blockUrl)):
        return this.detectBlockType(url, logger);

      default:
        logger?.warn(`Unknown login type: ${url}`);
        return WarpDriveLoginType.UNKNOWN;
    }
  }

  /**
   * Private helper method that specifically handles block URLs.
   * This method is called by detectLoginTypeFromUrl when a block URL is detected.
   *
   * It extracts the redirectUrl parameter from block URLs and analyzes the redirect
   * destination to determine the appropriate login type. This is different from
   * detectLoginTypeFromUrl because it specifically handles the URL parsing and
   * decoding logic for redirect scenarios.
   *
   * @param url - The block URL containing a redirectUrl parameter
   * @param logger - Optional logger for logging detection results
   * @returns The detected login type based on the redirect URL (WD_LOGIN, UNIFIED_LOGIN, HARD_BLOCK, or UNKNOWN)
   */
  private static detectBlockType(
    url: string,
    logger?: ILogger
  ): WarpDriveLoginType {
    try {
      const redirectUrl = new URL(url).searchParams.get('redirectUrl');

      if (!redirectUrl) {
        logger?.warn(
          'No redirectUrl found in connectivity URL, returning UNKNOWN'
        );
        return WarpDriveLoginType.HARD_BLOCK;
      }

      const decodedUrl = decodeURIComponent(redirectUrl);

      switch (true) {
        case decodedUrl.includes(WSBrokerServiceConstants.WD_LOGIN_URL_PATTERN):
          logger?.info(`Detected WD_LOGIN from URL: ${url}`);
          return WarpDriveLoginType.WD_LOGIN;
        case decodedUrl.includes(
          WSBrokerServiceConstants.UNIFIED_LOGIN_URL_PATTERN
        ):
          logger?.info(`Detected UNIFIED_LOGIN from URL: ${url}`);
          return WarpDriveLoginType.UNIFIED_LOGIN;
        default:
          logger?.warn('Unknown redirect pattern, defaulting to WD login');
          return WarpDriveLoginType.UNIFIED_LOGIN;
      }
    } catch {
      return WarpDriveLoginType.UNKNOWN;
    }
  }
}
